import { HISTORIC_SITES, ERA_LABELS, REGION_LABELS } from './historicSitesData.js';
import { COURSES, COMPARISONS, SITE_BY_ID, courseState, timelineStart } from './learningData.js';
import { element as el, button, selectControl, speechButton, briefSummary, sourceLinks } from './learningWidgets.js';
import { narration } from './narration.js';

export function createLearningUI({ progress, onFlyCourse, onFlySite, onClose, canFly = true }) {
  const dialog = document.getElementById('learning-dialog');
  const body = document.getElementById('learning-body');
  const tabs = document.getElementById('learning-tabs');
  const closeBtn = document.getElementById('learning-close');
  const count = document.getElementById('learning-count');
  let tab = 'courses';
  let selectedSite = null;
  let selectedCourse = null;
  let query = '', region = '', visitedOnly = false, era = '';
  let pair = [...COMPARISONS[0].ids];
  let comparison = 0;
  let reviewSites = [], reviewIndex = 0, reviewCorrect = 0, reviewAttempts = 0, reviewAnswered = false;
  let previousFocus = null;
  const labels = { courses: 'コース', book: '旅の図鑑', timeline: '年表', compare: 'くらべる', review: '復習' };

  function close() {
    if (!dialog.open) return;
    narration.stop();
    dialog.close();
    previousFocus?.focus?.({ preventScroll: true });
    onClose?.();
  }
  closeBtn.addEventListener('click', close);
  dialog.addEventListener('cancel', e => { e.preventDefault(); close(); });

  function openTab(next) {
    narration.stop();
    tab = next;
    selectedSite = null;
    selectedCourse = null;
    if (tab === 'review') { reviewSites = progress.reviewQueue(); reviewIndex = 0; reviewCorrect = 0; reviewAttempts = 0; reviewAnswered = false; }
    render();
    body.focus({ preventScroll: true });
  }
  for (const [key, text] of Object.entries(labels)) {
    const b = button(text, () => openTab(key), 'learning-tab');
    b.dataset.tab = key;
    tabs.append(b);
  }
  function status(site) {
    const p = progress.get(site.id);
    if (!p) return '未訪問';
    if (site.solemn) return '訪問の記録';
    return '訪問済み' + (p.observation ? ' · 観察済み' : '')
      + (p.q === null ? ' · クイズ未回答' : p.q === 1 ? ' · クイズ正解' : ' · 復習しよう')
      + (p.reviewedAt ? ' · 復習済み' : '');
  }
  function title(text, description) {
    body.append(el('h3', text));
    if (description) body.append(el('p', description, 'learning-lead'));
  }
  function flightButton(text, onClick) {
    const b = button(text, onClick, 'primary-btn');
    b.disabled = !canFly;
    if (!canFly) b.title = 'フライトには地図の接続設定が必要です。';
    return b;
  }
  function showSite(id) { selectedSite = id; narration.stop(); render(); body.focus(); }

  function renderCourses() {
    if (selectedCourse) { renderCourseResult(COURSES.find(c => c.id === selectedCourse)); return; }
    title('小さな旅に出よう', '1回5〜10分を目安に、景色から歴史を発見。途中で戻っても続きから飛べます。');
    const grid = el('div', undefined, 'course-grid');
    for (const course of COURSES) {
      const state = courseState(course, progress);
      const completed = state.nextIndex === course.siteIds.length;
      const card = el('article', undefined, 'course-card');
      card.append(el('p', `${course.emoji} ${course.region} · ${course.duration} · ${course.siteIds.length}地点`, 'eyebrow'));
      card.append(el('h4', course.name), el('p', course.description));
      const stops = el('ol', undefined, 'course-stops');
      course.siteIds.forEach((id, i) => stops.append(el('li', `${i < state.nextIndex ? '✓ ' : ''}${SITE_BY_ID.get(id).name}`)));
      card.append(stops, el('p', completed ? '✓ コース達成' : `${state.nextIndex} / ${course.siteIds.length} 地点を訪問`, 'learning-status'));
      const actions = el('div', undefined, 'learning-actions');
      actions.append(flightButton(completed ? 'もう一度飛ぶ' : state.nextIndex ? '続きから飛ぶ' : 'このコースを飛ぶ', () => {
        close(); onFlyCourse(course, { restart: completed });
      }));
      if (completed) actions.append(button('旅のふりかえり', () => { selectedCourse = course.id; render(); body.focus(); }));
      card.append(actions);
      grid.append(card);
    }
    body.append(grid);
  }
  function renderCourseResult(course) {
    const state = courseState(course, progress);
    title(`${course.name} · コース達成`, '速さや正答数ではなく、見つけたことを一つ持ち帰ろう。');
    body.append(button('コース一覧へ', () => { selectedCourse = null; render(); }));
    const grid = el('div', undefined, 'learning-grid');
    for (const id of course.siteIds) {
      const site = SITE_BY_ID.get(id);
      const card = el('article', undefined, 'learning-card');
      card.append(el('h4', site.name), el('p', site.yearLabel, 'learning-status'), el('p', briefSummary(site)));
      grid.append(card);
    }
    body.append(grid);
    const label = el('label', course.reflection, 'reflection-label');
    const input = document.createElement('textarea');
    input.rows = 3;
    input.maxLength = 600;
    input.value = state.reflection;
    input.placeholder = '一言でも大丈夫。あとから書き足せます。';
    label.append(input);
    const saved = el('p', '', 'learning-status');
    saved.setAttribute('role', 'status');
    body.append(label, button('ふりかえりを保存', () => {
      progress.saveCourse(course.id, { ...courseState(course, progress), reflection: input.value.trim() });
      saved.textContent = 'ふりかえりを記録しました。';
    }), saved);
  }
  function renderDetail(site) {
    body.append(button('一覧へ戻る', () => { selectedSite = null; narration.stop(); render(); body.focus(); }));
    title(`${site.emoji} ${site.name}`, site.nameEn);
    body.append(el('p', status(site), 'learning-status'));
    const facts = el('dl', undefined, 'site-facts');
    for (const [label, value] of [['年代', site.yearLabel], ['国・地域', site.country], ['文明・王朝', site.civilization], ['世界遺産', site.whcYear ? `${site.whcYear}年 ${site.whcNote ?? ''}` : '登録なし']]) {
      const row = el('div', undefined, 'fact-row'); row.append(el('dt', label), el('dd', value)); facts.append(row);
    }
    body.append(facts, el('p', site.summary, 'site-summary'));
    const actions = el('div', undefined, 'learning-actions');
    actions.append(speechButton(site.id, `${site.name}。${site.summary}`));
    actions.append(flightButton('この名所の近くから飛ぶ', () => { close(); onFlySite(site); }));
    if (progress.has(site.id) && site.quiz && !site.solemn) {
      const review = button('', () => {
        progress.requestReview(site.id, !progress.get(site.id)?.reviewRequested);
        update();
      });
      function update() {
        const requested = !!progress.get(site.id)?.reviewRequested;
        review.textContent = requested ? '✓ 復習リストから外す' : 'あとで復習';
        review.setAttribute('aria-pressed', String(requested));
      }
      update();
      actions.append(review, button('この問題を復習', () => {
        tab = 'review'; selectedSite = null; reviewSites = [site]; reviewIndex = 0; reviewCorrect = 0; reviewAttempts = 0; reviewAnswered = false; narration.stop(); render(); body.focus();
      }));
    }
    body.append(actions);
    if (site.sources?.length) { body.append(el('h4', 'もっと調べる'), sourceLinks(site)); }
  }
  function filterControls({ timeline = false } = {}) {
    const filters = el('div', undefined, 'learning-filters');
    const searchLabel = el('label', undefined, 'learning-field');
    searchLabel.append(el('span', '名所を検索'));
    const input = document.createElement('input');
    input.type = 'search'; input.value = query; input.placeholder = '名前・国・文明で探す';
    input.setAttribute('aria-label', '名所を検索');
    searchLabel.append(input);
    filters.append(searchLabel);
    const update = () => renderList(timeline);
    input.addEventListener('input', () => { query = input.value; update(); });
    filters.append(selectControl('地域', [['', 'すべての地域'], ...Object.entries(REGION_LABELS)], region, value => { region = value; update(); }));
    if (timeline) filters.append(selectControl('時代', [['', 'すべての時代'], ...Object.entries(ERA_LABELS)], era, value => { era = value; update(); }));
    const label = el('label', undefined, 'learning-check');
    const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.checked = visitedOnly;
    checkbox.addEventListener('change', () => { visitedOnly = checkbox.checked; update(); });
    label.append(checkbox, el('span', '訪問済みだけ')); filters.append(label);
    body.append(filters, el('p', '', 'list-count'), el('div', undefined, timeline ? 'timeline-list' : 'learning-grid'));
    renderList(timeline);
  }
  function renderList(timeline) {
    const list = body.querySelector(timeline ? '.timeline-list' : '.learning-grid');
    if (!list) return;
    const q = query.trim().toLocaleLowerCase();
    const sites = HISTORIC_SITES.filter(s => (!region || s.region === region) && (!timeline || !era || s.era === era)
      && (!visitedOnly || progress.has(s.id)) && `${s.name} ${s.nameEn} ${s.country} ${s.civilization}`.toLocaleLowerCase().includes(q));
    if (timeline) sites.sort((a, b) => (timelineStart(a) ?? Infinity) - (timelineStart(b) ?? Infinity));
    body.querySelector('.list-count').textContent = `${sites.length}件`;
    list.replaceChildren();
    if (!sites.length) { list.append(el('p', '該当する名所がありません。検索や絞り込みを変えてみてください。', 'empty-state')); return; }
    let unknownShown = false;
    for (const site of sites) {
      if (timeline && timelineStart(site) === null && !unknownShown) {
        list.append(el('h4', '年代に幅がある名所')); unknownShown = true;
      }
      const card = button('', () => showSite(site.id), timeline ? 'timeline-entry' : 'learning-card site-entry');
      card.append(el('span', ERA_LABELS[site.era], `site-badge era-${site.era}`), el('strong', `${site.emoji} ${site.name}`), el('span', site.yearLabel, 'timeline-date'), el('span', site.country, 'muted'), el('span', status(site), 'learning-status'));
      if (progress.has(site.id)) card.classList.add('discovered');
      list.append(card);
    }
  }
  function renderCompare() {
    title('遠く離れた名所をくらべよう', '同じ時代、似た役割、違う地形。共通点と違いを一つずつ見つけてみよう。');
    const themes = el('div', undefined, 'learning-actions');
    COMPARISONS.forEach((theme, index) => themes.append(button(theme.name, () => { comparison = index; pair = [...theme.ids]; narration.stop(); render(); }, comparison === index ? 'learning-btn selected' : 'learning-btn')));
    body.append(themes, el('p', comparison < 0 ? '選んだ二つの名所は、いつ、何のためにつくられた？ 地形や文化にも注目しよう。' : COMPARISONS[comparison].prompt, 'comparison-prompt'));
    const choices = HISTORIC_SITES.map(s => [s.id, s.name]);
    const selectors = el('div', undefined, 'learning-filters');
    pair.forEach((id, i) => selectors.append(selectControl(i ? 'くらべる名所' : '最初の名所', choices, id, value => { pair[i] = value; comparison = -1; narration.stop(); render(); })));
    body.append(selectors);
    if (pair[0] === pair[1]) body.append(el('p', '同じ名所を選んでいます。もう一方を変えると違いを比較できます。', 'learning-status'));
    const sites = pair.map(id => SITE_BY_ID.get(id));
    const table = el('table', undefined, 'comparison-table');
    const head = el('thead'); const header = el('tr'); header.append(el('th', '比べる点'));
    sites.forEach(s => header.append(el('th', s.name))); head.append(header); table.append(head);
    const tbody = el('tbody');
    for (const [label, get] of [['年代', s => s.yearLabel], ['地域', s => s.country], ['時代', s => ERA_LABELS[s.era]], ['文明・王朝', s => s.civilization]]) {
      const row = el('tr'); const th = el('th', label); th.scope = 'row'; row.append(th);
      sites.forEach(s => row.append(el('td', get(s)))); tbody.append(row);
    }
    table.append(tbody); body.append(table);
    const grid = el('div', undefined, 'comparison-grid');
    for (const site of sites) {
      const card = el('article', undefined, 'learning-card');
      card.append(el('h4', site.name), el('p', site.summary), speechButton(site.id, `${site.name}。${site.summary}`), button('図鑑で開く', () => { tab = 'book'; showSite(site.id); }));
      grid.append(card);
    }
    body.append(grid);
  }
  function renderReview() {
    title('今日の小さな復習', '答えを思い出してから解説へ。間違えても大丈夫。1回に最大3問です。');
    if (!reviewSites.length) {
      body.append(el('p', '今は復習待ちの問題がありません。訪問済みの名所を図鑑で開き、「あとで復習」に追加できます。', 'empty-state'), button('旅の図鑑へ', () => openTab('book'))); return;
    }
    if (reviewIndex >= reviewSites.length) {
      body.append(el('h4', reviewAttempts ? '復習おつかれさま！' : 'また時間のあるときに'), el('p', `${reviewAttempts}問に回答し、正解は${reviewCorrect}問。${reviewSites.length - reviewAttempts ? ` ${reviewSites.length - reviewAttempts}問を見送りました。` : ''}`), el('p', '正解した問題は日をあけてもう一度。気になる名所は図鑑で読み返せます。'), button('図鑑を開く', () => openTab('book'))); return;
    }
    const site = reviewSites[reviewIndex];
    body.append(el('p', `${reviewIndex + 1} / ${reviewSites.length} · ${site.name}`, 'learning-status'));
    const card = el('section', undefined, 'review-card');
    card.append(el('h4', site.quiz.statement));
    const options = el('div', undefined, 'site-quiz-btns');
    const result = el('div', undefined, 'review-result'); result.setAttribute('role', 'status');
    const next = button('次の問題へ', () => { narration.stop(); reviewIndex++; reviewAnswered = false; render(); body.focus(); }, 'primary-btn');
    next.hidden = true;
    for (const [label, choice] of [['○ 正しい', true], ['× 誤り', false]]) options.append(button(label, () => {
      if (reviewAnswered) return;
      reviewAnswered = true;
      reviewAttempts++;
      const correct = choice === site.quiz.answer;
      if (correct) reviewCorrect++;
      progress.setQuiz(site.id, correct, { review: true });
      for (const b of options.querySelectorAll('button')) b.disabled = true;
      result.append(el('h4', correct ? '正解！' : `正解は${site.quiz.answer ? '○' : '×'}。理由を確かめよう。`), el('p', site.quiz.explain), speechButton(`review-${site.id}`, site.quiz.explain));
      next.textContent = reviewIndex === reviewSites.length - 1 ? '復習を終える' : '次の問題へ';
      next.hidden = false; skip.hidden = true; next.focus();
    }, 'quiz-btn review-choice'));
    const skip = button('今回は見送る', () => { reviewIndex++; reviewAnswered = false; render(); body.focus(); });
    card.append(options, result, next, skip); body.append(card);
  }
  function render() {
    count.textContent = `訪問 ${progress.size} / ${HISTORIC_SITES.length}`;
    for (const b of tabs.children) { b.classList.toggle('selected', b.dataset.tab === tab); b.setAttribute('aria-pressed', String(b.dataset.tab === tab)); }
    body.replaceChildren(); body.scrollTop = 0;
    if (selectedSite) renderDetail(SITE_BY_ID.get(selectedSite));
    else if (tab === 'courses') renderCourses();
    else if (tab === 'book') { title('旅の図鑑', '訪れた名所と学びを集めよう。未訪問の名所も調べられます。'); filterControls(); }
    else if (tab === 'timeline') { title('世界を年表でたどる', '表示されている最初の出来事の年代順です。世紀や期間は目安として並べ、創建と再建を区別して読みます。'); filterControls({ timeline: true }); }
    else if (tab === 'compare') renderCompare();
    else renderReview();
    const speechStatus = el('p', narration.message, 'learning-status'); speechStatus.dataset.speechStatus = ''; speechStatus.setAttribute('role', 'status'); body.append(speechStatus);
  }
  return {
    open(next = 'courses', { completion } = {}) {
      previousFocus = document.activeElement;
      openTab(next);
      if (completion) { selectedCourse = completion; render(); }
      if (!dialog.open) dialog.showModal();
      body.focus({ preventScroll: true });
    },
    close,
    isOpen: () => dialog.open,
  };
}
